import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import {
  FULL_CROP,
  type Degrees,
  type NormCrop,
  cropImageBlob,
  nextRotation,
  rotateImageBlob,
} from "../lib/imageEdit";

interface Props {
  sourceUrl: string;
  sourceBlob: Blob;
  onCancel: () => void;
  onApply: (blob: Blob, previewUrl: string) => void;
}

type DragMode =
  | null
  | "move"
  | "n"
  | "s"
  | "e"
  | "w"
  | "nw"
  | "ne"
  | "sw"
  | "se";

const MIN_SIZE = 0.12;

interface ContentBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Visible content rect inside an object-fit: contain image, relative to the stage.
 */
function measureContainedImage(
  stage: HTMLElement,
  img: HTMLImageElement,
): ContentBox {
  const sr = stage.getBoundingClientRect();
  const nw = img.naturalWidth || 1;
  const nh = img.naturalHeight || 1;
  const stageW = sr.width;
  const stageH = sr.height;
  const scale = Math.min(stageW / nw, stageH / nh);
  const width = nw * scale;
  const height = nh * scale;
  return {
    left: (stageW - width) / 2,
    top: (stageH - height) / 2,
    width,
    height,
  };
}

/**
 * Mobile-first rotate + crop editor for receipt photos.
 */
export function ImageEditor({ sourceUrl, sourceBlob, onCancel, onApply }: Props) {
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [workingBlob, setWorkingBlob] = useState(sourceBlob);
  const [workingUrl, setWorkingUrl] = useState(sourceUrl);
  const [rotation, setRotation] = useState<Degrees>(0);
  const [crop, setCrop] = useState<NormCrop>({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
  const [busy, setBusy] = useState(false);
  const [imgBox, setImgBox] = useState<ContentBox>({ left: 0, top: 0, width: 0, height: 0 });

  const dragRef = useRef<{
    mode: DragMode;
    startX: number;
    startY: number;
    origin: NormCrop;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (workingUrl !== sourceUrl) URL.revokeObjectURL(workingUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const measureImage = useCallback(() => {
    const img = imgRef.current;
    const stage = stageRef.current;
    if (!img || !stage || !img.naturalWidth) return;
    setImgBox(measureContainedImage(stage, img));
  }, []);

  useEffect(() => {
    measureImage();
    // Remeasure after layout paints (iOS often needs a frame)
    const t1 = requestAnimationFrame(() => measureImage());
    const t2 = window.setTimeout(measureImage, 50);
    const t3 = window.setTimeout(measureImage, 200);

    const ro = new ResizeObserver(() => measureImage());
    if (stageRef.current) ro.observe(stageRef.current);
    if (imgRef.current) ro.observe(imgRef.current);
    window.addEventListener("resize", measureImage);
    window.addEventListener("orientationchange", measureImage);

    return () => {
      cancelAnimationFrame(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      ro.disconnect();
      window.removeEventListener("resize", measureImage);
      window.removeEventListener("orientationchange", measureImage);
    };
  }, [measureImage, workingUrl]);

  async function rotate(delta: 90 | -90) {
    setBusy(true);
    try {
      const deg = nextRotation(0, delta);
      const next = await rotateImageBlob(workingBlob, deg);
      const url = URL.createObjectURL(next);
      if (workingUrl !== sourceUrl) URL.revokeObjectURL(workingUrl);
      setWorkingBlob(next);
      setWorkingUrl(url);
      setRotation(nextRotation(rotation, delta));
      setCrop({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
      // measure after new image loads via onLoad
    } finally {
      setBusy(false);
    }
  }

  function clientToNorm(clientX: number, clientY: number): { x: number; y: number } {
    const stage = stageRef.current;
    if (!stage || imgBox.width <= 0 || imgBox.height <= 0) return { x: 0, y: 0 };
    const sr = stage.getBoundingClientRect();
    const x = (clientX - sr.left - imgBox.left) / imgBox.width;
    const y = (clientY - sr.top - imgBox.top) / imgBox.height;
    return {
      x: Math.min(1, Math.max(0, x)),
      y: Math.min(1, Math.max(0, y)),
    };
  }

  function onPointerDown(mode: DragMode, e: ReactPointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...crop },
    };
  }

  function onPointerMove(e: ReactPointerEvent) {
    const drag = dragRef.current;
    if (!drag || !drag.mode) return;
    e.preventDefault();

    const start = clientToNorm(drag.startX, drag.startY);
    const cur = clientToNorm(e.clientX, e.clientY);
    const dx = cur.x - start.x;
    const dy = cur.y - start.y;
    const o = drag.origin;
    let next: NormCrop = { ...o };

    const mode = drag.mode;
    if (mode === "move") {
      next.x = clamp(o.x + dx, 0, 1 - o.w);
      next.y = clamp(o.y + dy, 0, 1 - o.h);
    } else {
      let left = o.x;
      let top = o.y;
      let right = o.x + o.w;
      let bottom = o.y + o.h;

      if (mode.includes("w")) left = clamp(o.x + dx, 0, right - MIN_SIZE);
      if (mode.includes("e")) right = clamp(o.x + o.w + dx, left + MIN_SIZE, 1);
      if (mode.includes("n")) top = clamp(o.y + dy, 0, bottom - MIN_SIZE);
      if (mode.includes("s")) bottom = clamp(o.y + o.h + dy, top + MIN_SIZE, 1);

      next = { x: left, y: top, w: right - left, h: bottom - top };
    }
    setCrop(next);
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  async function apply() {
    setBusy(true);
    try {
      const nearlyFull =
        crop.x <= 0.001 && crop.y <= 0.001 && crop.w >= 0.999 && crop.h >= 0.999;
      const cropped = nearlyFull
        ? workingBlob
        : await cropImageBlob(workingBlob, crop);
      const url = URL.createObjectURL(cropped);
      onApply(cropped, url);
    } finally {
      setBusy(false);
    }
  }

  function resetCrop() {
    setCrop(FULL_CROP);
  }

  const ready = imgBox.width > 8 && imgBox.height > 8;

  const boxStyle: CSSProperties = ready
    ? {
        left: imgBox.left + crop.x * imgBox.width,
        top: imgBox.top + crop.y * imgBox.height,
        width: Math.max(8, crop.w * imgBox.width),
        height: Math.max(8, crop.h * imgBox.height),
      }
    : { display: "none" };

  return (
    <div className="image-editor">
      <p className="muted" style={{ margin: "0 0 0.65rem" }}>
        Drag the box to crop · use rotate if the receipt is sideways
      </p>

      <div
        className="image-editor-stage"
        ref={stageRef}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          ref={imgRef}
          src={workingUrl}
          alt="Edit receipt"
          className="image-editor-img"
          onLoad={measureImage}
          draggable={false}
        />

        <div
          className="image-editor-crop"
          style={boxStyle}
          onPointerDown={(e) => onPointerDown("move", e)}
        >
          <span className="handle nw" onPointerDown={(e) => onPointerDown("nw", e)} />
          <span className="handle ne" onPointerDown={(e) => onPointerDown("ne", e)} />
          <span className="handle sw" onPointerDown={(e) => onPointerDown("sw", e)} />
          <span className="handle se" onPointerDown={(e) => onPointerDown("se", e)} />
          <span className="handle n" onPointerDown={(e) => onPointerDown("n", e)} />
          <span className="handle s" onPointerDown={(e) => onPointerDown("s", e)} />
          <span className="handle e" onPointerDown={(e) => onPointerDown("e", e)} />
          <span className="handle w" onPointerDown={(e) => onPointerDown("w", e)} />
        </div>
      </div>

      <div className="image-editor-toolbar">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={busy}
          onClick={() => rotate(-90)}
          aria-label="Rotate left"
        >
          ↺ Rotate
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={busy}
          onClick={() => rotate(90)}
          aria-label="Rotate right"
        >
          Rotate ↻
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={busy}
          onClick={resetCrop}
        >
          Full frame
        </button>
      </div>

      <div className="camera-actions" style={{ marginTop: "0.75rem" }}>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          style={{ flex: 1 }}
          onClick={apply}
          disabled={busy || !ready}
        >
          {busy ? "Working…" : "Apply crop"}
        </button>
      </div>
    </div>
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
